import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import toast from 'react-hot-toast';
import {
  policyApi,
  PolicyResponse,
  ValidateResponse,
} from '../../services/api/policy';
import { PolicySimulation } from './PolicySimulation';
import { rbacApi, RoleMappingResponse } from '../../services/api/rbac';
import { UserRole, ROLE_LABELS } from '../../contexts/RoleContext';
import { useWebSocket } from '../../contexts/WebSocketContext';
// THUB-186: timezone-aware shared formatter (was new Date(x).toLocaleString()).
import { formatDateTime, activeTimezone } from '../../utils/formatDateTime';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys';

// Stable empty references: `?? []` would be a new array on every render.
const NO_MAPPINGS: RoleMappingResponse[] = [];
const NO_GROUPS: string[] = [];
const NO_POLICIES: PolicyResponse[] = [];

const ALL_ROLES: UserRole[] = ['viewer', 'operator', 'admin', 'system_admin'];

const ROLE_HIERARCHY: Record<string, number> = {
  viewer: 0,
  operator: 1,
  admin: 2,
  system_admin: 3,
};

/** Render simple markdown (bold, bullets, inline code, paragraphs) as JSX. */
function renderMarkdown(text: string): React.ReactNode {
  if (!text) return null;
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let listItems: string[] = [];
  let key = 0;

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={key++} className="list-disc list-inside space-y-1 text-sm text-neutral-700 dark:text-slate-300">
          {listItems.map((item, i) => <li key={i}>{formatInline(item)}</li>)}
        </ul>
      );
      listItems = [];
    }
  };

  const formatInline = (s: string): React.ReactNode => {
    // Bold: **text** and inline code: `text`
    const parts = s.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**'))
        return <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>;
      if (part.startsWith('`') && part.endsWith('`'))
        return <code key={i} className="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-xs font-mono">{part.slice(1, -1)}</code>;
      return part;
    });
  };

  let tableRows: string[][] = [];

  const flushTable = () => {
    if (tableRows.length > 1) {
      const header = tableRows[0];
      // THUB-293: the hyphen is LAST in this character class on purpose. Leading-hyphen order
      // makes the class read as a Tailwind arbitrary property (hyphen = a legal CSS property
      // name, pipe = its value), so Tailwind's content scanner emitted a junk declaration into
      // the stylesheet. CRA's cssnano passed that through; Vite's lightningcss rejects it as a
      // syntax error and fails the build. Trailing hyphen is a literal, so the regex is
      // unchanged. Note this applies to comments too -- the scanner reads raw file text.
      const body = tableRows.slice(1).filter(r => !r.every(c => /^[|:-]+$/.test(c)));
      elements.push(
        <div key={key++} className="overflow-x-auto rounded-lg border border-neutral-200 dark:border-slate-700">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="bg-neutral-50 dark:bg-slate-800">
                {header.map((cell, i) => (
                  <th key={i} className="px-3 py-2 text-left font-semibold text-neutral-700 dark:text-slate-300 border-b border-neutral-200 dark:border-slate-700">
                    {formatInline(cell.trim())}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {body.map((row, ri) => (
                <tr key={ri} className={ri % 2 === 0 ? 'bg-white dark:bg-slate-900' : 'bg-neutral-50/50 dark:bg-slate-800/50'}>
                  {row.map((cell, ci) => (
                    <td key={ci} className="px-3 py-1.5 text-neutral-600 dark:text-slate-400 border-b border-neutral-100 dark:border-slate-800">
                      {formatInline(cell.trim())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    tableRows = [];
  };

  for (const line of lines) {
    const trimmed = line.trim();

    // Table row: | col1 | col2 | ...
    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      flushList();
      const cells = trimmed.slice(1, -1).split('|');
      tableRows.push(cells);
      continue;
    } else if (tableRows.length > 0) {
      flushTable();
    }

    if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      listItems.push(trimmed.slice(2));
    } else if (trimmed.startsWith('## ')) {
      flushList();
      elements.push(
        <h4 key={key++} className="text-sm font-semibold text-neutral-800 dark:text-slate-200 mt-1">
          {formatInline(trimmed.slice(3))}
        </h4>
      );
    } else if (trimmed.startsWith('### ')) {
      flushList();
      elements.push(
        <h5 key={key++} className="text-sm font-medium text-neutral-700 dark:text-slate-300 mt-1">
          {formatInline(trimmed.slice(4))}
        </h5>
      );
    } else if (trimmed === '---') {
      flushList();
      elements.push(<hr key={key++} className="border-neutral-200 dark:border-slate-700" />);
    } else {
      flushList();
      if (trimmed !== '') {
        elements.push(
          <p key={key++} className="text-sm text-neutral-700 dark:text-slate-300 leading-relaxed">
            {formatInline(trimmed)}
          </p>
        );
      }
    }
  }
  flushList();
  flushTable();
  return <div className="space-y-2">{elements}</div>;
}

const ACTIONS: { value: string; label: string }[] = [
  { value: 'ListPartners', label: 'View Partners' },
  { value: 'CreatePartner', label: 'Create Partner' },
  { value: 'UpdatePartner', label: 'Edit Partner' },
  { value: 'DeletePartner', label: 'Delete Partner' },
  { value: 'ListConnections', label: 'View Connections' },
  { value: 'CreateConnection', label: 'Create Connection' },
  { value: 'UpdateConnection', label: 'Edit Connection' },
  { value: 'DeleteConnection', label: 'Delete Connection' },
  { value: 'ListTasks', label: 'View Tasks' },
  { value: 'CreateTask', label: 'Create Task' },
  { value: 'UpdateTask', label: 'Edit Task' },
  { value: 'DeleteTask', label: 'Delete Task' },
  { value: 'TestConnection', label: 'Test Connection' },
  { value: 'CreateTaskTransfer', label: 'Trigger Transfer' },
  { value: 'ListRoleMappings', label: 'Manage Role Mappings' },
];

/** Generate contextual example prompts based on current policies. */
function getExamplePrompts(policies: PolicyResponse[]): string[] {
  const hasOperatorPolicy = policies.some(p => p.cedarPolicy?.includes('Operator'));
  const hasAdminPolicy = policies.some(p => p.cedarPolicy?.includes('Admin'));
  const customCount = policies.filter(p => !p.isSystem).length;

  const pool: string[] = [
    // Restriction-focused (always relevant)
    'Prevent operators from deleting any resources',
    'Block viewers from accessing audit logs',
    // Role refinement
    hasOperatorPolicy ? 'What actions can operators currently perform?' : 'Give operators permission to test connections only',
    hasAdminPolicy ? 'Remove the ability for admins to delete partners' : 'Let admins manage tasks but not connections',
    // Explanation/audit
    'Explain the current permission structure',
    'What would happen if I removed the operator policy?',
    // Gap analysis
    customCount === 0 ? 'Suggest additional policies to tighten security' : 'Are there any permission gaps in the current setup?',
  ];

  // Shuffle and pick 3
  const shuffled = pool.sort(() => Math.random() - 0.5);
  return shuffled.slice(0, 3);
}

type SubTab = 'list' | 'ai' | 'editor';
type TestMode = 'role' | 'user';

/* -- SVG Icons ---------------------------------------------------------- */

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg className={className || 'w-4 h-4'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function SparklesIcon({ className }: { className?: string }) {
  return (
    <svg className={className || 'w-5 h-5'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l1.912 5.813a2 2 0 001.275 1.275L21 12l-5.813 1.912a2 2 0 00-1.275 1.275L12 21l-1.912-5.813a2 2 0 00-1.275-1.275L3 12l5.813-1.912a2 2 0 001.275-1.275L12 3z" />
      <path d="M5 3v4" />
      <path d="M3 5h4" />
      <path d="M19 17v4" />
      <path d="M17 19h4" />
    </svg>
  );
}

function CodeIcon({ className }: { className?: string }) {
  return (
    <svg className={className || 'w-4 h-4'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 18 22 12 16 6" />
      <polyline points="8 6 2 12 8 18" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className || 'w-4 h-4'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
    </svg>
  );
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className || 'w-5 h-5'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}

function XCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className || 'w-5 h-5'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="15" y1="9" x2="9" y2="15" />
      <line x1="9" y1="9" x2="15" y2="15" />
    </svg>
  );
}

function AlertTriangleIcon({ className }: { className?: string }) {
  return (
    <svg className={className || 'w-4 h-4'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className || 'w-4 h-4'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function CopyIcon({ className }: { className?: string }) {
  return (
    <svg className={className || 'w-4 h-4'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </svg>
  );
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className || 'w-4 h-4'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87" />
      <path d="M16 3.13a4 4 0 010 7.75" />
    </svg>
  );
}

function SendIcon({ className }: { className?: string }) {
  return (
    <svg className={className || 'w-4 h-4'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg className={className || 'w-4 h-4'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10" />
      <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
    </svg>
  );
}

function LoadingSpinner({ className }: { className?: string }) {
  return (
    <div className={`inline-block border-2 border-current border-t-transparent rounded-full animate-spin ${className || 'w-4 h-4'}`} />
  );
}

/* -- Chat message types ------------------------------------------------- */

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  cedarPolicy?: string;
  warnings?: string[];
  validation?: ValidateResponse | null;
  validated?: boolean;
  applied?: boolean;
  name?: string;
  description?: string;
  timestamp: Date;
}

let msgIdCounter = 0;
function nextMsgId(): string {
  return `msg-${++msgIdCounter}-${Date.now()}`;
}

/* -- Helper: resolve groups to highest role ------------------------------ */

function resolveGroupsToRole(
  groups: string[],
  mappings: RoleMappingResponse[]
): UserRole | null {
  let highestRole: UserRole | null = null;
  let highestRank = -1;

  for (const group of groups) {
    const mapping = mappings.find(
      (m) => m.groupName.toLowerCase() === group.toLowerCase()
    );
    if (mapping) {
      const rank = ROLE_HIERARCHY[mapping.role] ?? -1;
      if (rank > highestRank) {
        highestRank = rank;
        highestRole = mapping.role as UserRole;
      }
    }
  }

  return highestRole;
}

/* -- Sub-tab content components ----------------------------------------- */

function PolicyListTab({
  policies,
  loading,
  onDelete,
  onRefresh,
}: {
  policies: PolicyResponse[];
  loading: boolean;
  onDelete: (policyId: string) => void;
  onRefresh: () => void;
}) {
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [expandedPolicy, setExpandedPolicy] = useState<string | null>(null);
  const [testMode, setTestMode] = useState<TestMode>('role');
  const [testRole, setTestRole] = useState<string>(ALL_ROLES[0]);
  const [testAction, setTestAction] = useState<string>(ACTIONS[0].value);
  const [testResult, setTestResult] = useState<{
    decision: string;
    matchedPolicies: string[];
    resolvedRole?: string;
  } | null>(null);
  const [testing, setTesting] = useState(false);

  // "By User" mode state
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  // THUB-298: the "By User" read is a query enabled by the mode, not an effect guarded on the
  // state it sets.
  //
  // The old effect's condition was `testMode === 'user' && roleMappings.length === 0 &&
  // !loadingMappings` -- it read the very state it wrote, in order to fake "fetch once". `enabled`
  // expresses the laziness and the cache expresses the once-ness, with no self-reference.
  const mappingsQuery = useQuery({
    queryKey: queryKeys.rbac.roleMappingsFull,
    queryFn: () => rbacApi.getRoleMappingsFull(),
    enabled: testMode === 'user',
  });
  const roleMappings: RoleMappingResponse[] = mappingsQuery.data?.mappings ?? NO_MAPPINGS;
  const knownGroups: string[] = mappingsQuery.data?.knownGroups ?? NO_GROUPS;
  const loadingMappings = testMode === 'user' && mappingsQuery.isPending;
  const [groupSearch, setGroupSearch] = useState('');
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);


  const handleDelete = async (policyId: string) => {
    setDeleting(policyId);
    try {
      await policyApi.deletePolicy(policyId);
      onDelete(policyId);
      toast.success('Policy deleted');
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete policy');
    } finally {
      setDeleting(null);
      setConfirmDelete(null);
    }
  };

  const handleCopyPolicy = async (cedarPolicy: string) => {
    try {
      await navigator.clipboard.writeText(cedarPolicy);
      toast.success('Policy copied to clipboard');
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleTestByRole = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await policyApi.testPolicy(testRole, testAction);
      setTestResult(result);
    } catch (err: any) {
      toast.error(err.message || 'Failed to test authorization');
    } finally {
      setTesting(false);
    }
  };

  const handleTestByUser = async () => {
    if (selectedGroups.length === 0) {
      toast.error('Select at least one Cognito group');
      return;
    }
    const resolved = resolveGroupsToRole(selectedGroups, roleMappings);
    if (!resolved) {
      setTestResult({
        decision: 'DENY',
        matchedPolicies: [],
        resolvedRole: 'none (no matching role mapping)',
      });
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const result = await policyApi.testPolicy(resolved, testAction);
      setTestResult({
        ...result,
        resolvedRole: ROLE_LABELS[resolved] || resolved,
      });
    } catch (err: any) {
      toast.error(err.message || 'Failed to test authorization');
    } finally {
      setTesting(false);
    }
  };

  const handleTest = () => {
    if (testMode === 'role') {
      handleTestByRole();
    } else {
      handleTestByUser();
    }
  };

  const toggleGroup = (group: string) => {
    setSelectedGroups((prev) =>
      prev.includes(group) ? prev.filter((g) => g !== group) : [...prev, group]
    );
  };

  const filteredGroups = knownGroups.filter(
    (g) =>
      g.toLowerCase().includes(groupSearch.toLowerCase()) &&
      !selectedGroups.includes(g)
  );

  const toggleExpand = (policyId: string) => {
    setExpandedPolicy((prev) => (prev === policyId ? null : policyId));
  };

  return (
    <div className="space-y-6">
      {/* Policy Table */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-neutral-200 dark:border-slate-700 overflow-hidden">
        <div className="px-5 py-4 border-b border-neutral-200 dark:border-slate-700 flex items-center justify-between">
          <div>
            <h3 className="font-display font-semibold text-lg text-neutral-900 dark:text-white">
              Active Policies
            </h3>
            <p className="mt-0.5 text-sm text-neutral-500 dark:text-slate-400">
              {policies.length} polic{policies.length !== 1 ? 'ies' : 'y'} configured
            </p>
          </div>
          <button
            onClick={onRefresh}
            className="px-3 py-1.5 text-xs font-medium rounded-lg border border-neutral-300 dark:border-slate-600 text-neutral-700 dark:text-slate-300 hover:bg-neutral-50 dark:hover:bg-slate-700 transition-colors"
          >
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="px-5 py-16 text-center">
            <LoadingSpinner className="w-8 h-8 text-primary-500 mx-auto mb-3" />
            <p className="text-sm text-neutral-500 dark:text-slate-400">Loading policies...</p>
          </div>
        ) : policies.length === 0 ? (
          <div className="px-5 py-16 text-center">
            <ShieldIcon className="w-10 h-10 text-neutral-300 dark:text-slate-600 mx-auto mb-3" />
            <p className="text-neutral-500 dark:text-slate-400 text-sm">No policies configured yet.</p>
            <p className="text-neutral-400 dark:text-slate-500 text-xs mt-1">Use the AI Assistant or Cedar Editor to create your first policy.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-neutral-50 dark:bg-slate-800/50 border-b-2 border-neutral-200 dark:border-slate-700">
                  <th className="pl-5 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-slate-400 uppercase tracking-wider w-8" />
                  <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-slate-400 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-slate-400 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-slate-400 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-slate-400 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-slate-400 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-neutral-500 dark:text-slate-400 uppercase tracking-wider pr-5">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 dark:divide-slate-700/50">
                {policies.map((policy) => (
                  <React.Fragment key={policy.policyId}>
                    <tr
                      className="hover:bg-neutral-50 dark:hover:bg-slate-700/30 transition-colors cursor-pointer"
                      onClick={() => toggleExpand(policy.policyId)}
                    >
                      <td className="pl-5 py-3.5">
                        <ChevronDownIcon
                          className={`w-4 h-4 text-neutral-400 dark:text-slate-500 transition-transform duration-200 ${
                            expandedPolicy === policy.policyId ? 'rotate-180' : ''
                          }`}
                        />
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="font-semibold text-neutral-900 dark:text-slate-100">
                          {policy.name || policy.description || 'Untitled policy'}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="text-[11px] text-neutral-400 dark:text-slate-500 font-mono">
                          {policy.policyId}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="text-xs text-neutral-500 dark:text-slate-400" title={policy.description}>
                          {policy.description && policy.description.length > 60
                            ? policy.description.slice(0, 57) + '...'
                            : policy.description || '--'}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        {policy.isSystem ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                            <ShieldIcon className="w-3 h-3" />
                            System
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
                            Custom
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-neutral-500 dark:text-slate-400 text-xs">
                        {policy.createdAt
                          ? formatDateTime(policy.createdAt, activeTimezone(), 'dateOnly')
                          : '--'}
                      </td>
                      <td className="px-4 py-3.5 text-right pr-5" onClick={(e) => e.stopPropagation()}>
                        {policy.isSystem ? (
                          <span className="text-xs text-neutral-400 dark:text-slate-500">Protected</span>
                        ) : confirmDelete === policy.policyId ? (
                          <div className="flex items-center justify-end gap-2">
                            <span className="text-xs text-danger-600 dark:text-danger-400">Delete?</span>
                            <button
                              onClick={() => handleDelete(policy.policyId)}
                              disabled={deleting === policy.policyId}
                              className="px-2.5 py-1 text-xs font-medium rounded-lg bg-danger-600 hover:bg-danger-700 text-white disabled:opacity-50 transition-colors"
                            >
                              {deleting === policy.policyId ? 'Deleting...' : 'Confirm'}
                            </button>
                            <button
                              onClick={() => setConfirmDelete(null)}
                              className="px-2.5 py-1 text-xs font-medium rounded-lg border border-neutral-300 dark:border-slate-600 text-neutral-700 dark:text-slate-300 hover:bg-neutral-50 dark:hover:bg-slate-700 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmDelete(policy.policyId)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg text-danger-600 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/20 border border-danger-200 dark:border-danger-800 transition-colors"
                          >
                            <TrashIcon className="w-3 h-3" />
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                    {/* Expanded row: full description + Cedar policy */}
                    {expandedPolicy === policy.policyId && (
                      <tr>
                        <td colSpan={7} className="px-0 py-0">
                          <div className="mx-5 my-3 space-y-3">
                            {/* Full description if truncated */}
                            {policy.description && policy.description.length > 60 && (
                              <p className="text-sm text-neutral-600 dark:text-slate-400">
                                {policy.description}
                              </p>
                            )}
                            {/* Cedar policy code block */}
                            <div className="rounded-lg overflow-hidden border border-slate-700 dark:border-slate-600">
                              <div className="px-4 py-2 bg-slate-800 dark:bg-slate-950 border-b border-slate-700 dark:border-slate-800 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <CodeIcon className="w-3.5 h-3.5 text-slate-400" />
                                  <span className="text-xs font-medium text-slate-400">Cedar Policy</span>
                                </div>
                                <button
                                  onClick={() => handleCopyPolicy(policy.cedarPolicy)}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md text-slate-400 hover:text-slate-200 hover:bg-slate-700 transition-colors"
                                >
                                  <CopyIcon className="w-3.5 h-3.5" />
                                  Copy
                                </button>
                              </div>
                              <pre className="bg-slate-900 dark:bg-slate-950 p-4 overflow-x-auto">
                                <code className="text-sm font-mono text-green-400 leading-relaxed whitespace-pre">
                                  {policy.cedarPolicy}
                                </code>
                              </pre>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Test Authorization */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-neutral-200 dark:border-slate-700 overflow-hidden">
        <div className="px-5 py-4 border-b border-neutral-200 dark:border-slate-700">
          <h3 className="font-display font-semibold text-lg text-neutral-900 dark:text-white">
            Test Authorization
          </h3>
          <p className="mt-0.5 text-sm text-neutral-500 dark:text-slate-400">
            Check whether a role or user is allowed or denied a specific action.
          </p>
        </div>

        {/* Test Mode Tabs */}
        <div className="px-5 pt-4">
          <div className="flex gap-1 p-1 bg-neutral-100 dark:bg-slate-900/50 rounded-lg w-fit">
            <button
              onClick={() => { setTestMode('role'); setTestResult(null); }}
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium rounded-md transition-all duration-200 ${
                testMode === 'role'
                  ? 'bg-white dark:bg-slate-700 text-neutral-900 dark:text-white shadow-sm'
                  : 'text-neutral-600 dark:text-slate-400 hover:text-neutral-900 dark:hover:text-slate-200'
              }`}
            >
              <ShieldIcon className="w-3.5 h-3.5" />
              By Role
            </button>
            <button
              onClick={() => { setTestMode('user'); setTestResult(null); }}
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium rounded-md transition-all duration-200 ${
                testMode === 'user'
                  ? 'bg-white dark:bg-slate-700 text-neutral-900 dark:text-white shadow-sm'
                  : 'text-neutral-600 dark:text-slate-400 hover:text-neutral-900 dark:hover:text-slate-200'
              }`}
            >
              <UsersIcon className="w-3.5 h-3.5" />
              By User Groups
            </button>
          </div>
        </div>

        <div className="px-5 py-4">
          {testMode === 'role' ? (
            /* -- By Role mode ------------------------------------------------- */
            <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
              <div className="flex-1 w-full sm:w-auto">
                <label className="block text-xs font-medium text-neutral-600 dark:text-slate-400 mb-1.5">Role</label>
                <select
                  value={testRole}
                  onChange={(e) => setTestRole(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg text-sm bg-white dark:bg-slate-900 border border-neutral-300 dark:border-slate-600 text-neutral-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                >
                  {ALL_ROLES.map((r) => (
                    <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1 w-full sm:w-auto">
                <label className="block text-xs font-medium text-neutral-600 dark:text-slate-400 mb-1.5">Action</label>
                <select
                  value={testAction}
                  onChange={(e) => setTestAction(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg text-sm bg-white dark:bg-slate-900 border border-neutral-300 dark:border-slate-600 text-neutral-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                >
                  {ACTIONS.map((a) => (
                    <option key={a.value} value={a.value}>{a.label}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={handleTest}
                disabled={testing}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg bg-primary-600 hover:bg-primary-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {testing ? <LoadingSpinner className="w-4 h-4" /> : <ShieldIcon className="w-4 h-4" />}
                Test
              </button>
            </div>
          ) : (
            /* -- By User Groups mode ------------------------------------------ */
            <div className="space-y-4">
              {loadingMappings ? (
                <div className="flex items-center gap-2 text-sm text-neutral-500 dark:text-slate-400">
                  <LoadingSpinner className="w-4 h-4" />
                  Loading group mappings...
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-xs font-medium text-neutral-600 dark:text-slate-400 mb-1.5">
                      Cognito Groups
                    </label>
                    <p className="text-xs text-neutral-400 dark:text-slate-500 mb-2">
                      Select groups to simulate a user's membership and see what role they would resolve to.
                    </p>

                    {/* Selected groups as chips */}
                    {selectedGroups.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {selectedGroups.map((group) => (
                          <span
                            key={group}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800"
                          >
                            {group}
                            <button
                              onClick={() => toggleGroup(group)}
                              className="ml-0.5 hover:text-primary-900 dark:hover:text-primary-100 transition-colors"
                            >
                              <XCircleIcon className="w-3.5 h-3.5" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Group search / dropdown */}
                    <div className="relative">
                      <input
                        type="text"
                        value={groupSearch}
                        onChange={(e) => {
                          setGroupSearch(e.target.value);
                          setShowGroupDropdown(true);
                        }}
                        onFocus={() => setShowGroupDropdown(true)}
                        onBlur={() => setTimeout(() => setShowGroupDropdown(false), 200)}
                        placeholder="Search groups..."
                        className="w-full px-4 py-2.5 rounded-lg text-sm bg-white dark:bg-slate-900 border border-neutral-300 dark:border-slate-600 text-neutral-900 dark:text-slate-100 placeholder:text-neutral-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                      />
                      {showGroupDropdown && filteredGroups.length > 0 && (
                        <div className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto rounded-lg border border-neutral-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-lg">
                          {filteredGroups.map((group) => (
                            <button
                              key={group}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                toggleGroup(group);
                                setGroupSearch('');
                                setShowGroupDropdown(false);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-neutral-900 dark:text-slate-100 hover:bg-neutral-50 dark:hover:bg-slate-700 transition-colors"
                            >
                              {group}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
                    <div className="flex-1 w-full sm:w-auto">
                      <label className="block text-xs font-medium text-neutral-600 dark:text-slate-400 mb-1.5">Action</label>
                      <select
                        value={testAction}
                        onChange={(e) => setTestAction(e.target.value)}
                        className="w-full px-4 py-2.5 rounded-lg text-sm bg-white dark:bg-slate-900 border border-neutral-300 dark:border-slate-600 text-neutral-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                      >
                        {ACTIONS.map((a) => (
                          <option key={a.value} value={a.value}>{a.label}</option>
                        ))}
                      </select>
                    </div>
                    <button
                      onClick={handleTest}
                      disabled={testing || selectedGroups.length === 0}
                      className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg bg-primary-600 hover:bg-primary-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {testing ? <LoadingSpinner className="w-4 h-4" /> : <UsersIcon className="w-4 h-4" />}
                      Test
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Test Result */}
          {testResult && (
            <div className={`mt-4 p-4 rounded-lg border ${
              testResult.decision === 'ALLOW'
                ? 'bg-success-50 dark:bg-success-900/20 border-success-200 dark:border-success-800'
                : 'bg-danger-50 dark:bg-danger-900/20 border-danger-200 dark:border-danger-800'
            }`}>
              <div className="flex items-center gap-2">
                {testResult.decision === 'ALLOW' ? (
                  <CheckCircleIcon className="w-5 h-5 text-success-600 dark:text-success-400" />
                ) : (
                  <XCircleIcon className="w-5 h-5 text-danger-600 dark:text-danger-400" />
                )}
                <span className={`font-semibold text-sm ${
                  testResult.decision === 'ALLOW'
                    ? 'text-success-700 dark:text-success-300'
                    : 'text-danger-700 dark:text-danger-300'
                }`}>
                  {testResult.decision}
                </span>
              </div>
              {testResult.resolvedRole && (
                <p className="mt-2 text-xs text-neutral-600 dark:text-slate-400">
                  Resolved role: <span className="font-medium text-neutral-800 dark:text-slate-200">{testResult.resolvedRole}</span>
                </p>
              )}
              {testResult.matchedPolicies.length > 0 && (
                <div className="mt-2 space-y-1">
                  <p className="text-xs font-medium text-neutral-600 dark:text-slate-400">Matched policies:</p>
                  {testResult.matchedPolicies.map((pid, i) => {
                    const match = policies.find(p => p.policyId === pid);
                    return (
                      <p key={i} className="text-xs text-neutral-500 dark:text-slate-500 pl-2 border-l-2 border-neutral-200 dark:border-slate-700">
                        {match?.name || match?.description || pid}
                        {match?.isSystem && <span className="ml-1 text-amber-600 dark:text-amber-400">(System)</span>}
                      </p>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AIAssistantTab({
  policies,
  onPolicyCreated,
}: {
  policies: PolicyResponse[];
  onPolicyCreated: () => void;
}) {
  // Persist chat messages to sessionStorage so they survive route changes
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    try {
      const saved = sessionStorage.getItem('thub-ai-chat');
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.map((m: any) => ({ ...m, timestamp: new Date(m.timestamp) }));
      }
    } catch { /* ignore */ }
    return [];
  });
  const [input, setInput] = useState(() => sessionStorage.getItem('thub-ai-input') || '');

  // Save messages to sessionStorage on change
  useEffect(() => {
    try {
      sessionStorage.setItem('thub-ai-chat', JSON.stringify(messages));
    } catch { /* ignore */ }
  }, [messages]);

  useEffect(() => {
    try {
      sessionStorage.setItem('thub-ai-input', input);
    } catch { /* ignore */ }
  }, [input]);
  const [generating, setGenerating] = useState(() => {
    try { return sessionStorage.getItem('thub-ai-generating') === 'true'; } catch { return false; }
  });
  const examplePrompts = useMemo(() => getExamplePrompts(policies), [policies]);
  const [validatingId, setValidatingId] = useState<string | null>(null);
  const [applyingId, setApplyingId] = useState<string | null>(null);
  const [simulatingId, setSimulatingId] = useState<string | null>(null);
  const [showPolicies, setShowPolicies] = useState(false);
  const [editingName, setEditingName] = useState<string | null>(null);
  const [editingDescription, setEditingDescription] = useState<string | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesRef = useRef(messages);
  // THUB-298: written in an effect, not during render (react-hooks/refs). Mutating a ref while
  // rendering is not safe under concurrent rendering, and the only readers are async chat
  // callbacks, which run after commit either way.
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const scrollToBottom = useCallback(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || generating) return;

    const userMsg: ChatMessage = {
      id: nextMsgId(),
      role: 'user',
      content: text,
      timestamp: new Date(),
    };
    const withUserMsg = [...messages, userMsg];
    setMessages(withUserMsg);
    setInput('');
    setGenerating(true);
    // Persist immediately so navigating away keeps the user message
    try {
      sessionStorage.setItem('thub-ai-chat', JSON.stringify(withUserMsg));
      sessionStorage.setItem('thub-ai-input', '');
      sessionStorage.setItem('thub-ai-generating', 'true');
    } catch { /* ignore */ }

    try {
      // Build multi-turn conversation history for Bedrock
      const history = messages.map((m) => ({
        role: m.role,
        content: m.role === 'assistant'
          ? (m.cedarPolicy ? `${m.content}\n\n\`\`\`cedar\n${m.cedarPolicy}\n\`\`\`` : m.content)
          : m.content,
      }));
      const res = await policyApi.generatePolicy(text, history);

      const assistantMsg: ChatMessage = {
        id: nextMsgId(),
        role: 'assistant',
        content: res.explanation,
        cedarPolicy: res.cedarPolicy,
        warnings: res.warnings,
        name: text.length <= 60 ? text : text.slice(0, 57) + '...',
        description: res.explanation.slice(0, 200),
        timestamp: new Date(),
      };
      const updatedMessages = [...messagesRef.current, assistantMsg];
      setMessages(updatedMessages);
      // Also write directly to sessionStorage so it survives unmount
      try { sessionStorage.setItem('thub-ai-chat', JSON.stringify(updatedMessages)); } catch { /* ignore */ }
    } catch (err: any) {
      const errorMsg: ChatMessage = {
        id: nextMsgId(),
        role: 'assistant',
        content: err.message || 'Failed to generate policy. Please try again.',
        timestamp: new Date(),
      };
      const updatedMessages = [...messagesRef.current, errorMsg];
      setMessages(updatedMessages);
      try { sessionStorage.setItem('thub-ai-chat', JSON.stringify(updatedMessages)); } catch { /* ignore */ }
    } finally {
      setGenerating(false);
      try { sessionStorage.setItem('thub-ai-generating', 'false'); } catch { /* ignore */ }
      inputRef.current?.focus();
    }
  };

  const handleValidate = async (msgId: string) => {
    const msg = messages.find((m) => m.id === msgId);
    if (!msg?.cedarPolicy) return;
    setValidatingId(msgId);
    try {
      const res = await policyApi.validatePolicy(msg.cedarPolicy);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === msgId
            ? { ...m, validation: res, validated: res.valid }
            : m
        )
      );
      if (res.valid) {
        toast.success('Policy is valid');
      } else {
        toast.error('Policy has validation errors');
      }
    } catch (err: any) {
      toast.error(err.message || 'Validation failed');
    } finally {
      setValidatingId(null);
    }
  };

  const handleApply = async (msgId: string) => {
    const msg = messages.find((m) => m.id === msgId);
    if (!msg?.cedarPolicy || !msg.validated) return;
    setApplyingId(msgId);
    try {
      const name = msg.name || msg.content.slice(0, 60) || 'AI-generated policy';
      const desc = msg.description || msg.content.slice(0, 200) || '';
      await policyApi.createPolicy(msg.cedarPolicy, name, desc);
      setMessages((prev) =>
        prev.map((m) => (m.id === msgId ? { ...m, applied: true } : m))
      );
      toast.success('Policy created successfully');
      onPolicyCreated();
    } catch (err: any) {
      toast.error(err.message || 'Failed to create policy');
    } finally {
      setApplyingId(null);
    }
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleClearChat = () => {
    setMessages([]);
    setInput('');
    sessionStorage.removeItem('thub-ai-chat');
    sessionStorage.removeItem('thub-ai-input');
  };

  const handleExampleClick = (example: string) => {
    setInput(example);
    inputRef.current?.focus();
  };

  const updateName = (msgId: string, name: string) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === msgId ? { ...m, name } : m))
    );
    setEditingName(null);
  };

  const updateDescription = (msgId: string, desc: string) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === msgId ? { ...m, description: desc } : m))
    );
    setEditingDescription(null);
  };

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 280px)', minHeight: '500px' }}>
      {/* Collapsible Current Policies */}
      <div className="mb-4 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-neutral-200 dark:border-slate-700 overflow-hidden flex-shrink-0">
        <button
          onClick={() => setShowPolicies(!showPolicies)}
          className="w-full px-5 py-3 flex items-center justify-between hover:bg-neutral-50 dark:hover:bg-slate-700/30 transition-colors"
        >
          <div className="flex items-center gap-2">
            <ShieldIcon className="w-4 h-4 text-neutral-500 dark:text-slate-400" />
            <span className="text-sm font-medium text-neutral-700 dark:text-slate-300">
              Current Policies
            </span>
            <span className="text-xs text-neutral-400 dark:text-slate-500">
              ({policies.length})
            </span>
          </div>
          <ChevronDownIcon
            className={`w-4 h-4 text-neutral-400 dark:text-slate-500 transition-transform duration-200 ${
              showPolicies ? 'rotate-180' : ''
            }`}
          />
        </button>
        {showPolicies && (
          <div className="px-5 pb-4 border-t border-neutral-100 dark:border-slate-700/50">
            {policies.length === 0 ? (
              <p className="pt-3 text-xs text-neutral-400 dark:text-slate-500">
                No policies configured yet.
              </p>
            ) : (
              <div className="pt-3 space-y-2">
                {policies.map((p) => (
                  <div key={p.policyId} className="flex items-start gap-2">
                    <span className={`mt-0.5 inline-block w-2 h-2 rounded-full flex-shrink-0 ${
                      p.isSystem
                        ? 'bg-amber-400 dark:bg-amber-500'
                        : 'bg-primary-400 dark:bg-primary-500'
                    }`} />
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-neutral-700 dark:text-slate-300 truncate">
                        {p.name || p.description || 'Untitled policy'}
                      </p>
                      <code className="text-[10px] text-neutral-400 dark:text-slate-500 font-mono truncate block">
                        {p.cedarPolicy.slice(0, 60)}...
                      </code>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Chat container */}
      <div className="flex-1 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-neutral-200 dark:border-slate-700 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-5 py-3 border-b border-neutral-200 dark:border-slate-700 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 text-white">
              <SparklesIcon className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-display font-semibold text-neutral-900 dark:text-white">
                AI Policy Assistant
              </h3>
              <p className="text-xs text-neutral-500 dark:text-slate-400">
                Conversational Cedar policy generation
              </p>
            </div>
          </div>
          {messages.length > 0 && (
            <button
              onClick={handleClearChat}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg text-neutral-500 dark:text-slate-400 hover:bg-neutral-100 dark:hover:bg-slate-700 transition-colors"
            >
              <RefreshIcon className="w-3.5 h-3.5" />
              New conversation
            </button>
          )}
        </div>

        {/* Messages area */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {messages.length === 0 ? (
            /* Empty state with example prompts */
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="p-3 rounded-2xl bg-gradient-to-br from-violet-100 to-indigo-100 dark:from-violet-900/30 dark:to-indigo-900/30 mb-4">
                <SparklesIcon className="w-8 h-8 text-violet-600 dark:text-violet-400" />
              </div>
              <h4 className="font-display font-semibold text-neutral-900 dark:text-white mb-1">
                What policy do you need?
              </h4>
              <p className="text-sm text-neutral-500 dark:text-slate-400 mb-5 max-w-sm">
                Describe access rules in plain English. You can refine iteratively - ask follow-ups to adjust the generated policy.
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {examplePrompts.map((example) => (
                  <button
                    key={example}
                    onClick={() => handleExampleClick(example)}
                    className="px-3.5 py-2 text-xs rounded-lg border border-neutral-200 dark:border-slate-600 text-neutral-600 dark:text-slate-400 hover:bg-neutral-50 dark:hover:bg-slate-700 hover:border-neutral-300 dark:hover:border-slate-500 transition-colors text-left"
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            /* Chat messages */
            messages.map((msg) => (
              <div key={msg.id}>
                {msg.role === 'user' ? (
                  /* User message */
                  <div className="flex justify-end">
                    <div className="max-w-[85%] px-4 py-2.5 rounded-2xl rounded-br-md bg-primary-600 text-white">
                      <p className="text-sm leading-relaxed">{msg.content}</p>
                    </div>
                  </div>
                ) : (
                  /* Assistant message */
                  <div className="flex justify-start">
                    <div className="max-w-[90%] space-y-3">
                      {/* Avatar + explanation */}
                      <div className="flex items-start gap-2.5">
                        <div className="flex-shrink-0 mt-0.5 p-1 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 text-white">
                          <SparklesIcon className="w-3.5 h-3.5" />
                        </div>
                        <div className="pt-0.5">
                          {renderMarkdown(msg.content)}
                        </div>
                      </div>

                      {/* Cedar code block */}
                      {msg.cedarPolicy && (
                        <div className="ml-8 rounded-lg overflow-hidden border border-slate-700 dark:border-slate-600">
                          <div className="px-4 py-2 bg-slate-800 dark:bg-slate-950 border-b border-slate-700 dark:border-slate-800 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <CodeIcon className="w-3.5 h-3.5 text-slate-400" />
                              <span className="text-xs font-medium text-slate-400">Cedar Policy</span>
                            </div>
                            <button
                              onClick={() => handleCopy(msg.cedarPolicy!)}
                              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs text-slate-400 hover:text-slate-200 hover:bg-slate-700 rounded transition-colors"
                            >
                              <CopyIcon className="w-3 h-3" />
                              Copy
                            </button>
                          </div>
                          <pre className="bg-slate-900 dark:bg-slate-950 p-4 overflow-x-auto">
                            <code className="text-sm font-mono text-green-400 leading-relaxed whitespace-pre">
                              {msg.cedarPolicy}
                            </code>
                          </pre>
                        </div>
                      )}

                      {/* Warnings */}
                      {msg.warnings && msg.warnings.length > 0 && (
                        <div className="ml-8 space-y-1.5">
                          {msg.warnings.map((w, i) => (
                            <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                              <AlertTriangleIcon className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                              <span className="text-xs text-amber-700 dark:text-amber-300">{w}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Validation result */}
                      {msg.validation && (
                        <div className={`ml-8 p-3 rounded-lg border ${
                          msg.validation.valid
                            ? 'bg-success-50 dark:bg-success-900/20 border-success-200 dark:border-success-800'
                            : 'bg-danger-50 dark:bg-danger-900/20 border-danger-200 dark:border-danger-800'
                        }`}>
                          <div className="flex items-center gap-2">
                            {msg.validation.valid ? (
                              <>
                                <CheckCircleIcon className="w-4 h-4 text-success-600 dark:text-success-400" />
                                <span className="text-xs font-medium text-success-700 dark:text-success-300">Policy is valid</span>
                              </>
                            ) : (
                              <>
                                <XCircleIcon className="w-4 h-4 text-danger-600 dark:text-danger-400" />
                                <span className="text-xs font-medium text-danger-700 dark:text-danger-300">Validation errors</span>
                              </>
                            )}
                          </div>
                          {msg.validation.errors && msg.validation.errors.length > 0 && (
                            <ul className="mt-1.5 space-y-0.5">
                              {msg.validation.errors.map((e, i) => (
                                <li key={i} className="text-xs text-danger-600 dark:text-danger-400 ml-6">{e}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}

                      {/* Applied badge */}
                      {msg.applied && (
                        <div className="ml-8 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800">
                          <CheckCircleIcon className="w-4 h-4 text-success-600 dark:text-success-400" />
                          <span className="text-xs font-medium text-success-700 dark:text-success-300">Policy applied</span>
                        </div>
                      )}

                      {/* Action buttons for Cedar policies */}
                      {msg.cedarPolicy && !msg.applied && (
                        <div className="ml-8 space-y-2">
                          {/* Editable name and description */}
                          {msg.validated && simulatingId !== msg.id && (
                            <div className="space-y-1.5">
                              <div>
                                {editingName === msg.id ? (
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="text"
                                      defaultValue={msg.name || ''}
                                      onBlur={(e) => updateName(msg.id, e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') updateName(msg.id, (e.target as HTMLInputElement).value);
                                        if (e.key === 'Escape') setEditingName(null);
                                      }}
                                      autoFocus
                                      maxLength={100}
                                      placeholder="Short policy name..."
                                      className="flex-1 px-3 py-1.5 text-xs rounded-lg bg-white dark:bg-slate-900 border border-neutral-300 dark:border-slate-600 text-neutral-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                    />
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => setEditingName(msg.id)}
                                    className="text-xs text-neutral-500 dark:text-slate-400 hover:text-neutral-700 dark:hover:text-slate-300 transition-colors"
                                  >
                                    Name: <span className="font-medium underline decoration-dashed">{msg.name || 'Click to set'}</span>
                                  </button>
                                )}
                              </div>
                              <div>
                                {editingDescription === msg.id ? (
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="text"
                                      defaultValue={msg.description || ''}
                                      onBlur={(e) => updateDescription(msg.id, e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') updateDescription(msg.id, (e.target as HTMLInputElement).value);
                                        if (e.key === 'Escape') setEditingDescription(null);
                                      }}
                                      autoFocus
                                      placeholder="Policy description..."
                                      className="flex-1 px-3 py-1.5 text-xs rounded-lg bg-white dark:bg-slate-900 border border-neutral-300 dark:border-slate-600 text-neutral-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                    />
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => setEditingDescription(msg.id)}
                                    className="text-xs text-neutral-500 dark:text-slate-400 hover:text-neutral-700 dark:hover:text-slate-300 transition-colors"
                                  >
                                    Description: <span className="underline decoration-dashed">{msg.description || 'Click to set'}</span>
                                  </button>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Simulation panel (shown after validate, before apply) */}
                          {simulatingId === msg.id && msg.cedarPolicy && (
                            <PolicySimulation
                              cedarPolicy={msg.cedarPolicy}
                              onConfirm={() => {
                                setSimulatingId(null);
                                handleApply(msg.id);
                              }}
                              onCancel={() => setSimulatingId(null)}
                            />
                          )}

                          {simulatingId !== msg.id && (
                            <div className="flex items-center gap-2">
                              {!msg.validated && (
                                <button
                                  onClick={() => handleValidate(msg.id)}
                                  disabled={validatingId === msg.id}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-neutral-300 dark:border-slate-600 text-neutral-700 dark:text-slate-300 hover:bg-neutral-50 dark:hover:bg-slate-700 disabled:opacity-50 transition-colors"
                                >
                                  {validatingId === msg.id ? (
                                    <LoadingSpinner className="w-3.5 h-3.5" />
                                  ) : (
                                    <ShieldIcon className="w-3.5 h-3.5" />
                                  )}
                                  Validate
                                </button>
                              )}
                              {msg.validated && (
                                <button
                                  onClick={() => setSimulatingId(msg.id)}
                                  disabled={applyingId === msg.id}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-primary-600 hover:bg-primary-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  {applyingId === msg.id ? (
                                    <LoadingSpinner className="w-3.5 h-3.5" />
                                  ) : (
                                    <CheckCircleIcon className="w-3.5 h-3.5" />
                                  )}
                                  Simulate
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}

          {/* Generating indicator */}
          {generating && (
            <div className="flex justify-start">
              <div className="flex items-center gap-2.5">
                <div className="flex-shrink-0 p-1 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 text-white">
                  <SparklesIcon className="w-3.5 h-3.5" />
                </div>
                <div className="flex items-center gap-1.5 px-4 py-2.5 rounded-2xl rounded-bl-md bg-neutral-100 dark:bg-slate-700/50">
                  <div className="flex gap-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-neutral-400 dark:bg-slate-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-1.5 h-1.5 rounded-full bg-neutral-400 dark:bg-slate-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-1.5 h-1.5 rounded-full bg-neutral-400 dark:bg-slate-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                  <span className="text-xs text-neutral-500 dark:text-slate-400 ml-1">Generating...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={chatEndRef} />
        </div>

        {/* Input area */}
        <div className="flex-shrink-0 px-5 py-3 border-t border-neutral-200 dark:border-slate-700 bg-neutral-50 dark:bg-slate-800/50">
          <div className="flex items-end gap-3">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder={
                messages.length === 0
                  ? 'Describe the access rule you want to create...'
                  : 'Refine, ask a follow-up, or describe another policy...'
              }
              rows={1}
              className="flex-1 px-4 py-2.5 rounded-xl text-sm bg-white dark:bg-slate-900 border border-neutral-300 dark:border-slate-600 text-neutral-900 dark:text-slate-100 placeholder:text-neutral-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none max-h-32 overflow-y-auto"
              style={{ minHeight: '42px' }}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height = `${Math.min(target.scrollHeight, 128)}px`;
              }}
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || generating}
              className="flex-shrink-0 p-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
            >
              <SendIcon className="w-4 h-4" />
            </button>
          </div>
          <p className="mt-1.5 text-[10px] text-neutral-400 dark:text-slate-500">
            Enter to send, Shift+Enter for new line
          </p>
        </div>
      </div>
    </div>
  );
}

function CedarEditorTab({ onPolicyCreated }: { onPolicyCreated: () => void }) {
  const [cedarPolicy, setCedarPolicy] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [validation, setValidation] = useState<ValidateResponse | null>(null);
  const [validating, setValidating] = useState(false);
  const [, setApplying] = useState(false);
  const [showSimulation, setShowSimulation] = useState(false);

  const handleValidate = async () => {
    if (!cedarPolicy.trim()) return;
    setValidating(true);
    setShowSimulation(false);
    try {
      const res = await policyApi.validatePolicy(cedarPolicy.trim());
      setValidation(res);
      if (res.valid) {
        toast.success('Policy is valid');
      } else {
        toast.error('Policy has validation errors');
      }
    } catch (err: any) {
      toast.error(err.message || 'Validation failed');
    } finally {
      setValidating(false);
    }
  };

  const handleApply = async () => {
    if (!cedarPolicy.trim() || !name.trim()) return;
    setApplying(true);
    try {
      await policyApi.createPolicy(cedarPolicy.trim(), name.trim(), description.trim());
      toast.success('Policy created successfully');
      onPolicyCreated();
      setCedarPolicy('');
      setName('');
      setDescription('');
      setValidation(null);
      setShowSimulation(false);
    } catch (err: any) {
      toast.error(err.message || 'Failed to create policy');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-neutral-200 dark:border-slate-700 overflow-hidden">
        <div className="px-5 py-4 border-b border-neutral-200 dark:border-slate-700">
          <div className="flex items-center gap-2.5">
            <CodeIcon className="w-5 h-5 text-neutral-500 dark:text-slate-400" />
            <div>
              <h3 className="font-display font-semibold text-lg text-neutral-900 dark:text-white">
                Cedar Policy Editor
              </h3>
              <p className="text-sm text-neutral-500 dark:text-slate-400">
                Write Cedar policy language directly.
              </p>
            </div>
          </div>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-neutral-600 dark:text-slate-400 mb-1.5">
              Name <span className="text-danger-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              placeholder="e.g., Operator Task Access"
              className="w-full px-4 py-2.5 rounded-lg text-sm bg-white dark:bg-slate-900 border border-neutral-300 dark:border-slate-600 text-neutral-900 dark:text-slate-100 placeholder:text-neutral-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-600 dark:text-slate-400 mb-1.5">
              Description
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., Restrict operator delete access to prevent accidental data loss"
              className="w-full px-4 py-2.5 rounded-lg text-sm bg-white dark:bg-slate-900 border border-neutral-300 dark:border-slate-600 text-neutral-900 dark:text-slate-100 placeholder:text-neutral-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-neutral-600 dark:text-slate-400 mb-1.5">
              Cedar Policy
            </label>
            <textarea
              value={cedarPolicy}
              onChange={(e) => {
                setCedarPolicy(e.target.value);
                setValidation(null);
                setShowSimulation(false);
              }}
              placeholder={`permit (\n  principal in TransferHub::Role::"operator",\n  action in [TransferHub::Action::"ViewPartner"],\n  resource\n);`}
              rows={12}
              className="w-full px-4 py-3 rounded-lg text-sm font-mono bg-slate-900 dark:bg-slate-950 border border-slate-700 dark:border-slate-800 text-green-400 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none leading-relaxed"
            />
            <p className="mt-1.5 text-xs text-neutral-400 dark:text-slate-500">
              Cedar uses <code className="font-mono text-xs bg-neutral-100 dark:bg-slate-800 px-1 py-0.5 rounded">permit</code> and <code className="font-mono text-xs bg-neutral-100 dark:bg-slate-800 px-1 py-0.5 rounded">forbid</code> statements with principal, action, and resource clauses.
            </p>
          </div>

          {/* Validation result */}
          {validation && (
            <div className={`p-3 rounded-lg border ${
              validation.valid
                ? 'bg-success-50 dark:bg-success-900/20 border-success-200 dark:border-success-800'
                : 'bg-danger-50 dark:bg-danger-900/20 border-danger-200 dark:border-danger-800'
            }`}>
              <div className="flex items-center gap-2">
                {validation.valid ? (
                  <>
                    <CheckCircleIcon className="w-5 h-5 text-success-600 dark:text-success-400" />
                    <span className="text-sm font-medium text-success-700 dark:text-success-300">Policy is valid</span>
                  </>
                ) : (
                  <>
                    <XCircleIcon className="w-5 h-5 text-danger-600 dark:text-danger-400" />
                    <span className="text-sm font-medium text-danger-700 dark:text-danger-300">Validation errors</span>
                  </>
                )}
              </div>
              {validation.errors && validation.errors.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {validation.errors.map((e, i) => (
                    <li key={i} className="text-xs text-danger-600 dark:text-danger-400 ml-7">{e}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Simulation panel */}
          {showSimulation && cedarPolicy.trim() && (
            <PolicySimulation
              cedarPolicy={cedarPolicy.trim()}
              onConfirm={() => {
                setShowSimulation(false);
                handleApply();
              }}
              onCancel={() => setShowSimulation(false)}
            />
          )}

          {/* Action buttons: Validate → Simulate → (simulation panel handles Confirm & Apply) */}
          {!showSimulation && (
            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={handleValidate}
                disabled={!cedarPolicy.trim() || !name.trim() || validating}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-neutral-300 dark:border-slate-600 text-neutral-700 dark:text-slate-300 hover:bg-neutral-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {validating ? <LoadingSpinner className="w-4 h-4" /> : <ShieldIcon className="w-4 h-4" />}
                Validate
              </button>
              {validation?.valid && (
                <button
                  onClick={() => setShowSimulation(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 hover:bg-primary-700 text-white transition-colors"
                >
                  <CheckCircleIcon className="w-4 h-4" />
                  Simulate
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* -- Main Component ----------------------------------------------------- */

export function PolicyManagement() {
  const [activeTab, setActiveTab] = useState<SubTab>('list');
  // Locally-removed policy ids, so an optimistic delete shows immediately without copying the
  // whole list into state. Cleared implicitly on the next refetch, since the server list is then
  // authoritative and no longer contains them.
  const [deletedPolicyIds, setDeletedPolicyIds] = useState<Set<string>>(new Set());
  const { subscribe, connectionStatus } = useWebSocket();

  // THUB-298: the policy list is a query; `fetchPolicies` is now just its refetch, kept so the
  // policy_changes subscription and the Refresh button call sites read unchanged.
  const policiesQuery = useQuery({
    queryKey: queryKeys.policy.list,
    queryFn: () => policyApi.listPolicies(),
  });
  const policies: PolicyResponse[] = useMemo(
    () => (policiesQuery.data ?? NO_POLICIES).filter((p) => !deletedPolicyIds.has(p.policyId)),
    [policiesQuery.data, deletedPolicyIds]
  );
  const loading = policiesQuery.isPending;

  const fetchPolicies = useCallback(async () => {
    const result = await policiesQuery.refetch();
    if (result.error) {
      toast.error((result.error as Error).message || 'Failed to load policies');
    }
  }, [policiesQuery]);


  // Live-refresh policies when another user makes changes
  useEffect(() => {
    if (connectionStatus !== 'connected') return;
    const unsub = subscribe('policy_changes', () => {
      fetchPolicies();
    });
    return unsub;
  }, [connectionStatus, subscribe, fetchPolicies]);

  const handlePolicyDeleted = (policyId: string) => {
    setDeletedPolicyIds((prev) => new Set(prev).add(policyId));
  };

  const handlePolicyCreated = () => {
    fetchPolicies();
    setActiveTab('list');
  };

  const SUB_TABS: { key: SubTab; label: string; icon: React.ReactNode }[] = [
    {
      key: 'list',
      label: 'Policy List',
      icon: <ShieldIcon className="w-4 h-4" />,
    },
    {
      key: 'ai',
      label: 'AI Assistant',
      icon: <SparklesIcon className="w-4 h-4" />,
    },
    {
      key: 'editor',
      label: 'Cedar Editor',
      icon: <CodeIcon className="w-4 h-4" />,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Sub-tab navigation */}
      <div className="flex gap-1 p-1 bg-neutral-100 dark:bg-slate-800/80 rounded-lg w-fit">
        {SUB_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`
              inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200
              ${activeTab === tab.key
                ? 'bg-white dark:bg-slate-700 text-neutral-900 dark:text-white shadow-sm'
                : 'text-neutral-600 dark:text-slate-400 hover:text-neutral-900 dark:hover:text-slate-200'
              }
            `}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content — all stay mounted to preserve state (chat history, etc.) */}
      <div className={activeTab === 'list' ? '' : 'hidden'}>
        <PolicyListTab
          policies={policies}
          loading={loading}
          onDelete={handlePolicyDeleted}
          onRefresh={fetchPolicies}
        />
      </div>
      <div className={activeTab === 'ai' ? '' : 'hidden'}>
        <AIAssistantTab policies={policies} onPolicyCreated={handlePolicyCreated} />
      </div>
      <div className={activeTab === 'editor' ? '' : 'hidden'}>
        <CedarEditorTab onPolicyCreated={handlePolicyCreated} />
      </div>
    </div>
  );
}

export default PolicyManagement;
